<template>
 <div class="clients-edit">
    <div class="clients-edit__tabs">
      <router-link class="clients-edit__tabs_tab"
                   :class="{'clients-edit__tabs_tab_error': isGIError}"
                   :to="{ name: 'GeneralInfoTab' }"
                   active-class="clients-edit__tabs_tab_active">General Info</router-link>
      <router-link class="clients-edit__tabs_tab"
                   :class="{'clients-edit__tabs_tab_error': isRRTError}"
                   :to="{ name: 'RankReportTargetingTab' }"
                   active-class="clients-edit__tabs_tab_active">Rank Report Targeting</router-link>
      <router-link class="clients-edit__tabs_tab"
                   :to="{ name: 'MSVTab' }"
                   active-class="clients-edit__tabs_tab_active">Monthly Search Volume</router-link>
      <button v-if="isUserSuperAdmin" @click.prevent="triggerAgg" class="clients-edit__tabs_btn clients-edit__button clients-edit__button_submit">
        Trigger New Aggregation
      </button>
    </div>

   <div v-if="!isDataFetched">
     <div class="clients-edit__no-data">Awaiting Data</div>
   </div>
   <template v-else>
     <div class="clients-edit__content-wrapper">
       <router-view />
     </div>
   </template>

   <div v-if="isDataFetched" class="clients-edit__controls">
     <button class="clients-edit__button clients-edit__button_cancel"
             @click.prevent="handleClose">
       Cancel
     </button>
     <button v-if="!isAddNew" class="clients-edit__button clients-edit__button_delete"
             @click.prevent="showConfirmModal">
       Delete
     </button>
     <button class="clients-edit__button clients-edit__button_submit"
             @click.prevent="apply">
       Save
     </button>
   </div>

   <div class="clients-edit__update-loader" v-if="isDataUpdating">
     <base-loader class="clients-edit__update-loader-ring"/>
   </div>

   <modal name="confirm-modal" v-bind="modalProps">
     <base-confirm-modal title="Are you sure you want to delete this?"
                         subtitle="This Client will be removed permanently!"
                         @confirm="confirmModal"/>
   </modal>
 </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import baseModalProps from '@/common/BaseModalProps'
import ToasterService from '@/services/toasterService'

export default {
  name: 'Edit',
  destroyed () {
    this.resetClientData()
  },
  data () {
    return {
      clientDataModel: {
        id: -1,
        is_active: 1,
        name: '',
        email: '',
        agency: -1,
        audience_key: -1,
        guideline: '',
        msv: {
          msv_locale_1: '',
          msv_locale_2: '',
          msv_locale_3: '',
          msv_locale_4: ''
        },
        rank_report_targeting: {
          geo: -1,
          locale: 184,
          device: 1
        },
        scheduled_rank_report_start_date: null,
        scheduled_rank_report_day_interval: null,
        scheduled_rank_report_next_date: null,
        trial_end_date: null,
        automated_http_status_update: 1
      },
      isDataFetched: false,
      isDataUpdating: false,
      errorFields: {}
    }
  },
  watch: {
    clientId: {
      async handler (v) {
        if (v) {
          const queries = []
          !this.isSettingsQueried && queries.push(this.getClientsSettings(v))
          !this.isAddNew && queries.push(this.loadData(false))
          await Promise.all(queries)
          this.isDataFetched = true
        }
      },
      immediate: true
    }
  },
  computed: {
    ...mapState('client', ['clientData', 'isSettingsQueried']),
    selectedClientId () {
      return this.$route.params.id
    },
    isAddNew () {
      return this.selectedClientId === 'add-new-client'
    },
    modalProps () {
      return { ...baseModalProps, height: 'auto', width: '510px' }
    },
    isRRTError () {
      const errorFields = Object.keys(this.errorFields)
      if (!errorFields.length) return false
      return ['geo', 'locale', 'device'].some(v => errorFields.includes(v))
    },
    isGIError () {
      const errorFields = Object.keys(this.errorFields)
      if (!errorFields.length) return false
      return ['name', 'email', 'agency', 'audience_key', 'scheduled_rank_report_start_date', 'scheduled_rank_report_day_interval', 'trial_end_date'].some(v => errorFields.includes(v))
    },
    isUserSuperAdmin () {
      return this.userRoles.includes('admin')
    }
  },
  methods: {
    ...mapActions('client', ['resetClientData', 'createClientAggregation', 'getDataByClient', 'getClientsSettings', 'deleteClientById', 'createUpdateClient']),
    async loadData (updateLoadingStatus = true) {
      updateLoadingStatus && (this.isDataUpdating = true)
      await this.getDataByClient(this.selectedClientId)
      this.clientDataModel = { ...this.clientData }
      updateLoadingStatus && (this.isDataUpdating = false)
      return true
    },
    handleClose () {
      this.$router.push({ name: 'clients' })
    },
    showConfirmModal () {
      this.$modal.show('confirm-modal')
    },
    confirmModal (status) {
      this.$modal.hide('confirm-modal')
      if (status) {
        this.deleteClient()
      }
    },
    async triggerAgg () {
      this.isDataUpdating = true
      await this.createClientAggregation(this.selectedClientId)
      this.isDataUpdating = false
    },
    async deleteClient () {
      try {
        this.isDataUpdating = true
        await this.deleteClientById(this.selectedClientId)
        this.handleClose()
      } catch (e) {
        this.isDataUpdating = false
      }
    },
    async apply () {
      this.isDataUpdating = true
      this.clientDataModel.name = this.clientDataModel.name?.trim() ?? ''
      this.clientDataModel.email = this.clientDataModel.email?.trim() ?? ''
      const response = await this.createUpdateClient({
        method: this.isAddNew ? 'post' : 'patch',
        clientId: this.selectedClientId,
        clientData: JSON.stringify(this.clientDataModel)
      })
      this.isDataUpdating = false

      if (!Object.keys(response).length) return false

      this.errorFields = response.error_fields

      if (!response.is_successful) {
        Object.values(this.errorFields).forEach(v => {
          ToasterService.showToast(ToasterService.ERROR_TYPE, v)
        })

        return null
      }

      const successMessage = `Client ${this.isAddNew ? 'created' : 'updated'} successfully`
      ToasterService.showToast(ToasterService.SUCCESS_TYPE, successMessage)
      if (this.isAddNew) {
        this.clientDataModel.id = response._id
        const currentRoutes = this.$route.path.split('/')
        this.$router.push({ path: `/system/clients/${response.id}/${currentRoutes[currentRoutes.length - 1]}` })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .clients-edit {
    padding: 25px 40px 0;
    position: relative;

    .v--modal-overlay {
      background: none;
    }

    &__controls {
      display: flex;
      justify-content: flex-end;
      margin-top: 30px;
    }

    &__button {
      width: 100px;
      height: 40px;
      border-radius: 4px;
      font-size: 10px;
      font-weight: 900;
      text-transform: uppercase;
      margin-left: 30px;
      border: none;
      outline: none;
      cursor: pointer;

      &_submit {
        background-color: $rfx-medium-march;
        color: #fff;
      }

      &_delete {
        background-color: $rfx-red;
        color: #fff
      }

      &_disabled {
        pointer-events: none;
        background-color: #fff;
        color: $rfx-neutral-grey;
        cursor: default;
      }

      &_cancel {
        color: $rfx-dark-march;
        background-color: #fff;
      }
    }

    &__tabs {
      display: flex;
      margin-bottom: -1px;

      &_btn {
        margin-left: auto;
      }
      &_tab {
        width: max-content;
        padding: 16px 9px;
        text-align: center;
        font-size: 14px;
        color: $rfx-light-march;
        background-color: transparent;
        border: 1px solid transparent;
        text-decoration: none;

        &_error {
          color: $rfx-red;
        }

        &_active {
          background-color: #fff;
          color: $rfx-dark-march;
          font-weight: bold;
          border: 1px solid $rfx-light-steel;
          border-bottom: 1px solid #fff;
        }
      }
    }

    &__update-loader {
      position: absolute;
      display: flex;
      width: 100%;
      height: 100%;
      justify-content: center;
      background-color: white;
      top: 74px;
      opacity: 0.5;
      z-index: 10000;
    }

    &__update-loader-ring {
      margin-top: 200px;
    }

    &__no-data {
      display: flex;
      height: 300px;
      background-color: #fff;
      border: 1px solid $rfx-light-steel;
      align-items: center;
      justify-content: center;
      font-size: 27px;
      font-weight: 600;
      color: $rfx-medium-grey;
      flex: 1 1;
    }

    &__content-wrapper {
      padding: 10px 40px 40px;
      border: 1px solid $rfx-light-steel;
      background-color: #fff;
    }

    &::v-deep .form {
      &__input {
        display: flex;
        flex-direction: column;
        position: relative;
        margin-top: 30px;

        &_invalid[type='text'] {
          border: 1px solid $rfx-red;
          border-radius: 4px;
        }
      }

      &__error-message {
        color: $rfx-red;
        font-size: 10px;
        margin-top: 5px;
      }

      &__note {
        font-size: 14px;
        color: $rfx-dark-march;
        font-weight: normal;
        padding: 0;
        margin: 0;
      }

      &__label {
        display: block;
        font-weight: 600;
        margin-bottom: 5px;
        color: $rfx-dark-march;
      }

      &__field {
        width: -webkit-fill-available;
        height: 50px;
        padding: 0 20px;
        border: 2px solid $rfx-light-steel;
        outline: none;
        border-radius: 4px;
        font-size: 14px;
        color: $rfx-dark-march;

        &-wrapper {
        }

        &-field[type='text'] {
          width: 100%;
          box-sizing: border-box;
          padding-right: 60px;
          font-weight: 500;
          @include ellipsis;
        }

        &_error {
          border-color: $rfx-red;
          color: $rfx-red;

          &::placeholder {
            color: $rfx-red;
            font-size: 13px;
          }
        }
      }
    }
  }
</style>
